const SB_STORAGE_MODE = 'sb-mode';
const SB_STORAGE_CODE = 'sb-discount-code';

function getSbCampaigns() {
  return window.themeVariables?.settings?.sbModeCampaigns || [];
}

function getSbCampaignByCode(code) {
  if (!code) return null;
  const normalized = code.toLowerCase();
  return getSbCampaigns().find((campaign) => campaign.code.toLowerCase() === normalized) || null;
}

function getSbState() {
  const modeEnabled = localStorage.getItem(SB_STORAGE_MODE) === 'true';
  const code = localStorage.getItem(SB_STORAGE_CODE) || '';
  const campaign = getSbCampaignByCode(code);

  return {
    active: modeEnabled && campaign !== null,
    code,
    percentage: campaign?.percentage || 0,
    campaign
  };
}

function activateSbMode(code) {
  const campaign = getSbCampaignByCode(code);
  if (!campaign) return false;

  localStorage.setItem(SB_STORAGE_MODE, 'true');
  localStorage.setItem(SB_STORAGE_CODE, campaign.code);
  return true;
}

function deactivateSbMode() {
  localStorage.removeItem(SB_STORAGE_MODE);
  localStorage.removeItem(SB_STORAGE_CODE);
}

const __sbExclusionCache = {
  handle: Object.create(null),
  product: Object.create(null),
  variant: Object.create(null),
  variantHandle: Object.create(null)
};
const __sbExclusionFetches = Object.create(null);

function setSbExclusionCache({ handle, productId, variantId, variantIds, excluded }) {
  const value = Boolean(excluded);
  if (handle) __sbExclusionCache.handle[String(handle)] = value;
  if (productId != null && productId !== '') __sbExclusionCache.product[String(productId)] = value;
  if (variantId != null && variantId !== '') {
    __sbExclusionCache.variant[String(variantId)] = value;
    if (handle) __sbExclusionCache.variantHandle[String(variantId)] = String(handle);
  }
  if (Array.isArray(variantIds)) {
    variantIds.forEach((id) => {
      if (id == null || id === '') return;
      __sbExclusionCache.variant[String(id)] = value;
      if (handle) __sbExclusionCache.variantHandle[String(id)] = String(handle);
    });
  }
}

function getSbExclusionFromCache({ handle, productId, variantId }) {
  if (handle != null && handle !== '' && Object.prototype.hasOwnProperty.call(__sbExclusionCache.handle, String(handle))) {
    return __sbExclusionCache.handle[String(handle)];
  }
  if (productId != null && productId !== '' && Object.prototype.hasOwnProperty.call(__sbExclusionCache.product, String(productId))) {
    return __sbExclusionCache.product[String(productId)];
  }
  if (variantId != null && variantId !== '' && Object.prototype.hasOwnProperty.call(__sbExclusionCache.variant, String(variantId))) {
    return __sbExclusionCache.variant[String(variantId)];
  }
  return null;
}

function seedSbExclusionCacheFromDom() {
  document.querySelectorAll('[data-sb-excluded]').forEach((el) => {
    const excluded = el.dataset.sbExcluded === 'true';
    const productId = el.dataset.productId || null;
    const variantId = el.dataset.variantId || null;
    const handle = el.getAttribute('handle')
      || el.closest?.('product-card')?.getAttribute('handle')
      || null;
    setSbExclusionCache({ handle, productId, variantId, excluded });
  });

  document.querySelectorAll('wishlist-product-card[data-sb-excluded]').forEach((card) => {
    const excluded = card.dataset.sbExcluded === 'true';
    const handle = card.dataset.sbHandle || getWishlistCardHandle(card);
    const variant = getWishlistSelectedVariant(card);
    setSbExclusionCache({
      handle,
      variantId: variant?.id || null,
      excluded
    });
  });
}

function getSbExclusionFromDom({ handle, productId, variantId }) {
  if (productId != null && productId !== '') {
    const wrapper = document.querySelector(`[data-sb-price-wrapper][data-product-id="${productId}"]`);
    if (wrapper?.hasAttribute('data-sb-excluded')) {
      const excluded = wrapper.dataset.sbExcluded === 'true';
      setSbExclusionCache({ productId, handle, excluded });
      return excluded;
    }
  }

  if (variantId != null && variantId !== '') {
    const lineItem = document.querySelector(`[data-sb-line-item][data-variant-id="${variantId}"]`);
    if (lineItem?.hasAttribute('data-sb-excluded')) {
      const excluded = lineItem.dataset.sbExcluded === 'true';
      setSbExclusionCache({ variantId, productId, handle, excluded });
      return excluded;
    }

    const idFields = document.querySelectorAll('input[name="id"], select[name="id"]');
    for (const input of idFields) {
      if (String(input.value) !== String(variantId)) continue;

      const form = input.closest('form');
      if (form?.querySelector('[data-sb-campaign-disabled]')) {
        setSbExclusionCache({ variantId, productId: form.dataset.productId, excluded: true });
        return true;
      }

      const formProductId = form?.dataset?.productId;
      if (formProductId) {
        const wrapper = document.querySelector(`[data-sb-price-wrapper][data-product-id="${formProductId}"]`);
        if (wrapper?.hasAttribute('data-sb-excluded')) {
          const excluded = wrapper.dataset.sbExcluded === 'true';
          setSbExclusionCache({ variantId, productId: formProductId, handle, excluded });
          return excluded;
        }
      }

      const excludedHost = input.closest('[data-sb-excluded]');
      if (excludedHost) {
        const excluded = excludedHost.dataset.sbExcluded === 'true';
        setSbExclusionCache({
          variantId,
          productId: formProductId || excludedHost.dataset.productId,
          handle: excludedHost.getAttribute('handle') || handle,
          excluded
        });
        return excluded;
      }
    }

    const wishlistCards = document.querySelectorAll('wishlist-product-card');
    for (const card of wishlistCards) {
      const selected = getWishlistSelectedVariant(card);
      if (selected && String(selected.id) === String(variantId) && card.hasAttribute('data-sb-excluded')) {
        const excluded = card.dataset.sbExcluded === 'true';
        setSbExclusionCache({
          variantId,
          handle: card.dataset.sbHandle || getWishlistCardHandle(card) || handle,
          excluded
        });
        return excluded;
      }
    }
  }

  if (handle) {
    const card = document.querySelector(`product-card[handle="${CSS.escape(handle)}"]`);
    if (card?.hasAttribute('data-sb-excluded')) {
      const excluded = card.dataset.sbExcluded === 'true';
      setSbExclusionCache({ handle, excluded });
      return excluded;
    }

    const wishlistCard = document.querySelector(`wishlist-product-card[data-sb-handle="${CSS.escape(handle)}"]`);
    if (wishlistCard?.hasAttribute('data-sb-excluded')) {
      const excluded = wishlistCard.dataset.sbExcluded === 'true';
      setSbExclusionCache({ handle, excluded });
      return excluded;
    }
  }

  return null;
}

function parseSbExcludedFromHtml(html) {
  if (!html || typeof html !== 'string') return null;

  const wrapperMatch = html.match(/data-sb-price-wrapper[^>]*\sdata-sb-excluded="(true|false)"/i)
    || html.match(/data-sb-excluded="(true|false)"[^>]*\sdata-sb-price-wrapper/i);
  if (wrapperMatch) return wrapperMatch[1] === 'true';

  const resultMatch = html.match(/data-sb-exclusion-result[^>]*\sdata-sb-excluded="(true|false)"/i)
    || html.match(/data-sb-excluded="(true|false)"[^>]*\sdata-sb-exclusion-result/i);
  if (resultMatch) return resultMatch[1] === 'true';

  const cardMatch = html.match(/<product-card\b[^>]*\sdata-sb-excluded="(true|false)"/i);
  if (cardMatch) return cardMatch[1] === 'true';

  const anyMatch = html.match(/data-sb-excluded="(true|false)"/i);
  if (anyMatch) return anyMatch[1] === 'true';

  return null;
}

function parseSbExclusionMetaFromHtml(html, fallbackHandle) {
  const excluded = parseSbExcludedFromHtml(html);
  if (excluded === null) return null;

  const productIdMatch = html.match(/data-product-id="(\d+)"/i);
  const handleMatch = html.match(/data-handle="([^"]+)"/i);
  const variantIdsMatch = html.match(/data-variant-ids="([^"]*)"/i);
  const variantIds = variantIdsMatch?.[1]
    ? variantIdsMatch[1].split(',').map((id) => id.trim()).filter(Boolean)
    : [];

  return {
    excluded,
    productId: productIdMatch?.[1] || null,
    handle: handleMatch?.[1] || fallbackHandle,
    variantIds
  };
}

function getSbExclusionFromProductObject(product) {
  if (!product || typeof product !== 'object') return null;

  if (typeof product.sb_excluded === 'boolean') return product.sb_excluded;
  if (product.sb_excluded === 'true') return true;
  if (product.sb_excluded === 'false') return false;

  const readValue = (raw) => {
    if (raw == null) return null;
    if (typeof raw === 'boolean') return raw;
    if (typeof raw === 'string') {
      if (raw === 'true') return true;
      if (raw === 'false') return false;
      return null;
    }
    if (typeof raw === 'object') {
      if (typeof raw.value === 'boolean') return raw.value;
      if (raw.value === 'true') return true;
      if (raw.value === 'false') return false;
    }
    return null;
  };

  const custom = product.metafields?.custom;
  if (custom && typeof custom === 'object' && !Array.isArray(custom)) {
    const fromCustom = readValue(custom['2026_excluded'] ?? custom['2026-excluded']);
    if (fromCustom !== null) return fromCustom;
  }

  if (Array.isArray(product.metafields)) {
    const entry = product.metafields.find((metafield) => (
      metafield?.key === '2026_excluded'
      || (metafield?.namespace === 'custom' && metafield?.key === '2026_excluded')
    ));
    const fromList = readValue(entry?.value ?? entry);
    if (fromList !== null) return fromList;
  }

  return null;
}

async function fetchSbExclusionByHandle(handle) {
  if (!handle) return null;

  const cached = getSbExclusionFromCache({ handle });
  if (cached !== null) return cached;

  if (__sbExclusionFetches[handle]) return __sbExclusionFetches[handle];

  __sbExclusionFetches[handle] = (async () => {
    const root = window.Shopify?.routes?.root || '/';
    const cacheResult = (meta) => {
      if (!meta) return null;
      setSbExclusionCache({
        handle: meta.handle || handle,
        productId: meta.productId,
        variantIds: meta.variantIds,
        excluded: meta.excluded
      });
      return meta.excluded;
    };

    try {
      // 1) Section Rendering API — most reliable with OS 2.0 product JSON templates
      try {
        const sectionResponse = await fetch(`${root}products/${handle}?section_id=sb-product-exclusion`, {
          headers: { Accept: 'application/json' }
        });
        if (sectionResponse.ok) {
          const payload = await sectionResponse.json();
          const sectionHtml = payload?.['sb-product-exclusion'] || Object.values(payload || {})[0] || '';
          const meta = parseSbExclusionMetaFromHtml(sectionHtml, handle);
          if (meta) return cacheResult(meta);
        }
      } catch {
        // Fall through to alternate strategies.
      }

      // 2) Lightweight alternate product view
      try {
        const metaResponse = await fetch(`${root}products/${handle}?view=sb-meta`);
        if (metaResponse.ok) {
          const text = (await metaResponse.text()).trim();
          const jsonStart = text.indexOf('{');
          const jsonEnd = text.lastIndexOf('}');
          if (jsonStart !== -1 && jsonEnd !== -1) {
            const data = JSON.parse(text.slice(jsonStart, jsonEnd + 1));
            if (typeof data.excluded === 'boolean') {
              return cacheResult({
                excluded: data.excluded,
                productId: data.id,
                handle: data.handle || handle,
                variantIds: data.variant_ids || []
              });
            }
          }
          const meta = parseSbExclusionMetaFromHtml(text, handle);
          if (meta) return cacheResult(meta);
        }
      } catch {
        // Fall through.
      }

      // 3) Full product page HTML — PDP already renders data-sb-excluded
      const pageResponse = await fetch(`${root}products/${handle}`);
      if (!pageResponse.ok) return null;
      const pageHtml = await pageResponse.text();
      const meta = parseSbExclusionMetaFromHtml(pageHtml, handle);
      return cacheResult(meta);
    } catch (error) {
      console.warn('SB Mode: exclusion lookup failed', error);
      return null;
    } finally {
      delete __sbExclusionFetches[handle];
    }
  })();

  return __sbExclusionFetches[handle];
}

function getWishlistCardProduct(card) {
  if (!card) return null;
  const item = card.wishlistItem || card.item || card.productCard || null;
  return item?.product || card.product || null;
}

function findSbHandleForVariantId(variantId) {
  if (variantId == null || variantId === '') return null;

  const cachedHandle = __sbExclusionCache.variantHandle[String(variantId)];
  if (cachedHandle) return cachedHandle;

  const wishlistCards = document.querySelectorAll('wishlist-product-card');
  for (const card of wishlistCards) {
    const root = getWishlistCardRoot(card);
    const idInput = root.querySelector?.('input[name="id"], select[name="id"]');
    if (idInput && String(idInput.value) === String(variantId)) {
      return card.dataset.sbHandle || getWishlistCardHandle(card);
    }

    const selected = getWishlistSelectedVariant(card);
    if (selected && String(selected.id) === String(variantId)) {
      return card.dataset.sbHandle || getWishlistCardHandle(card);
    }

    const product = getWishlistCardProduct(card);
    if (product?.handle && Array.isArray(product.variants)) {
      if (product.variants.some((variant) => String(variant.id) === String(variantId))) {
        return product.handle;
      }
    }
  }

  const input = [...document.querySelectorAll('input[name="id"], select[name="id"]')]
    .find((field) => String(field.value) === String(variantId));
  return input?.closest('product-card')?.getAttribute('handle') || null;
}

async function resolveSbExclusion({ handle = null, productId = null, variantId = null, product = null } = {}) {
  seedSbExclusionCacheFromDom();

  const cached = getSbExclusionFromCache({ handle, productId, variantId });
  if (cached !== null) return cached;

  const fromProduct = getSbExclusionFromProductObject(product);
  if (fromProduct !== null) {
    setSbExclusionCache({
      handle: handle || product?.handle || null,
      productId: productId || product?.id || null,
      variantId,
      variantIds: Array.isArray(product?.variants) ? product.variants.map((variant) => variant.id) : null,
      excluded: fromProduct
    });
    return fromProduct;
  }

  const fromDom = getSbExclusionFromDom({ handle, productId, variantId });
  if (fromDom !== null) return fromDom;

  const resolvedHandle = handle
    || product?.handle
    || findSbHandleForVariantId(variantId);

  if (resolvedHandle) {
    const fetched = await fetchSbExclusionByHandle(resolvedHandle);
    if (fetched !== null) return fetched;
    // Lookup failed — unknown (do not treat as included).
    return null;
  }

  // No handle/product identity available yet.
  return null;
}

function getWishlistCardHandle(card) {
  if (!card) return null;
  if (card.dataset.sbHandle) return card.dataset.sbHandle;

  const product = getWishlistCardProduct(card);
  if (product?.handle) {
    card.dataset.sbHandle = product.handle;
    return product.handle;
  }

  const root = getWishlistCardRoot(card);
  const link = root.querySelector?.('a[href*="/products/"]')
    || card.querySelector?.('a[href*="/products/"]');
  const href = link?.getAttribute('href') || '';
  const match = href.match(/\/products\/([^/?#]+)/);
  const handle = match?.[1] ? decodeURIComponent(match[1]) : null;
  if (handle) card.dataset.sbHandle = handle;
  return handle;
}

async function ensureWishlistCardExclusion(card) {
  if (!(card instanceof Element)) return null;

  const handle = getWishlistCardHandle(card);
  const product = getWishlistCardProduct(card);
  const variant = getWishlistSelectedVariant(card);
  const root = getWishlistCardRoot(card);
  const idInput = root?.querySelector?.('input[name="id"], select[name="id"]');
  const variantId = variant?.id || idInput?.value || null;

  // Wishlist King may still be hydrating — caller should retry.
  if (!handle && !product?.id) return null;

  const excluded = await resolveSbExclusion({
    handle,
    productId: product?.id || null,
    variantId,
    product
  });

  // Only persist definitive answers so failed lookups are not cached as "included".
  if (excluded === null) {
    delete card.dataset.sbExcluded;
    return null;
  }

  card.dataset.sbExcluded = excluded ? 'true' : 'false';
  if (handle) card.dataset.sbHandle = handle;
  if (variantId) {
    setSbExclusionCache({ handle, productId: product?.id, variantId, excluded });
  }

  return excluded;
}

function getSbVariantPricing(salePrice, compareAtPrice, campaignPercentage) {
  const discountedPrice = Math.round(salePrice * (100 - campaignPercentage) / 100);
  const compareDisplay = compareAtPrice > salePrice ? compareAtPrice : salePrice;
  const savingsAmount = compareDisplay - discountedPrice;
  const effectivePercentage = compareDisplay > 0
    ? Math.round(savingsAmount * 100 / compareDisplay)
    : campaignPercentage;

  return {
    salePrice,
    compareDisplay,
    discountedPrice,
    savingsAmount,
    effectivePercentage
  };
}

function getSbVariantPricingFromVariant(variant, campaignPercentage) {
  const salePrice = variant.price;
  const compareAtPrice = variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
  return getSbVariantPricing(salePrice, compareAtPrice, campaignPercentage);
}

function getSbMoneyFormat() {
  const settings = window.themeVariables?.settings;
  if (settings?.currencyCodeEnabled && settings.moneyWithCurrencyFormat) {
    return settings.moneyWithCurrencyFormat;
  }
  return settings?.moneyFormat || '{{amount}}';
}

function formatSbMoney(cents) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const moneyFormat = getSbMoneyFormat();

  if (typeof window.formatMoney === 'function') {
    return window.formatMoney(cents, moneyFormat);
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = moneyFormat;

  function defaultTo(value, defaultValue) {
    return value == null || value !== value ? defaultValue : value;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ',');
    decimal = defaultTo(decimal, '.');
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    const parts = String(number).split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsAmount = parts[1] ? decimal + parts[1] : '';
    return dollarsAmount + centsAmount;
  }

  const match = formatString.match(placeholderRegex);
  let value = '';
  switch (match ? match[1] : 'amount') {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', '.');
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    case 'amount_no_decimals_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 0, "'");
      break;
    default:
      value = formatWithDelimiters(cents, 2);
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

function applySbSalePriceClasses(element, onSale) {
  if (!element) return;

  element.classList.toggle('text-on-sale', onSale);
  // Keep place typography (h4/h6/etc); only swap sale vs subdued color classes.
  if (onSale) {
    element.classList.remove('text-subdued');
  } else if (element.classList.contains('h4')) {
    element.classList.add('text-subdued');
  }
}

function formatSbSavingsLabel(pricing) {
  const discountMode = window.themeVariables?.settings?.discountMode || 'percentage';

  if (discountMode === 'percentage') {
    return `${pricing.effectivePercentage}%`;
  }

  return formatSbMoney(pricing.savingsAmount);
}

function formatSbBadgeHtml(savings) {
  const template = window.themeVariables?.strings?.discountBadge || 'Save @@';
  return template.replace('@@', savings);
}

function formatSbLineItemBadgeHtml(savings) {
  const template = window.themeVariables?.strings?.discountBadgeLineItem || '-@@';
  return template.replace('@@', savings);
}

function applySbLineItemBadges(lineItem, pricing, state) {
  const excluded = lineItem.dataset.sbExcluded === 'true';
  const saleBadges = lineItem.querySelectorAll('on-sale-badge[data-sb-cart-sale-badge], on-sale-badge[data-sb-native-sale-badge], [data-sb-native-sale-badge]');
  const campaignBadges = lineItem.querySelectorAll('[data-sb-campaign-badge]');
  const badgeLists = lineItem.querySelectorAll('badge-list');

  if (!state.active || excluded || pricing.savingsAmount <= 0) {
    return false;
  }

  const badgeHtml = formatSbLineItemBadgeHtml(formatSbSavingsLabel(pricing));
  let shown = false;

  // Prefer the real on-sale-badge in the cart title row.
  if (saleBadges.length) {
    saleBadges.forEach((badge) => {
      badge.classList.remove('onsale-test-badge');
      badge.innerHTML = badgeHtml;
      badge.hidden = false;
      badge.removeAttribute('hidden');
      shown = true;
    });
    campaignBadges.forEach((badge) => {
      badge.hidden = true;
      badge.setAttribute('hidden', '');
    });
  } else {
    campaignBadges.forEach((badge) => {
      badge.classList.remove('onsale-test-badge');
      badge.textContent = badgeHtml;
      badge.hidden = false;
      badge.removeAttribute('hidden');
      shown = true;
    });
  }

  if (shown) {
    badgeLists.forEach((list) => {
      list.hidden = false;
      list.removeAttribute('hidden');
    });
  }

  return shown;
}

function applySbLineItemSaleLook(lineItem, discountedCents, compareCents, state) {
  const saleEl = lineItem.querySelector('[data-sb-line-sale-el]');
  const compareEl = lineItem.querySelector('[data-sb-line-compare-el]');
  const priceList = saleEl?.closest('price-list') || lineItem;
  const excluded = lineItem.dataset.sbExcluded === 'true';

  if (!saleEl) return;

  updateSbPriceElementContent(saleEl, discountedCents);
  applySbSalePriceClasses(saleEl, true);

  if (compareEl) {
    if (compareCents > discountedCents) {
      updateSbPriceElementContent(compareEl, compareCents);
      compareEl.hidden = false;
      compareEl.removeAttribute('hidden');
      compareEl.classList.add('text-subdued', 'line-through');
      compareEl.classList.remove('text-on-sale');
    } else {
      compareEl.hidden = true;
      compareEl.setAttribute('hidden', '');
    }
  }

  const pricing = {
    compareDisplay: compareCents,
    discountedPrice: discountedCents,
    savingsAmount: Math.max(compareCents - discountedCents, 0),
    effectivePercentage: compareCents > 0
      ? Math.round(Math.max(compareCents - discountedCents, 0) * 100 / compareCents)
      : 0
  };

  if (state.active && !excluded && pricing.savingsAmount > 0) {
    applySbLineItemBadges(lineItem, pricing, state);
    // Keep price-list campaign badges hidden via CSS class cleanup / duplicate suppression
    priceList.querySelectorAll('[data-sb-campaign-badge].onsale-test-badge').forEach((badge) => {
      badge.hidden = true;
      badge.setAttribute('hidden', '');
    });
    setSbNativeSaleBadgesHidden(priceList, true);
    updateSbStickySave(priceList, pricing);
    updateSbStickySave(lineItem, pricing);
  }
}

function formatSbFromPrice(money) {
  const template = window.themeVariables?.strings?.fromPriceHtml || '@@';
  return template.replace('@@', money);
}

function getSbBadgeScope(element) {
  return element.closest('badge-list')
    || element.closest('[data-sb-price-wrapper]')
    || element.closest('price-list')
    || element.closest('[data-sb-line-item]')
    || element.parentElement;
}

function setSbNativeSaleBadgesHidden(scope, hidden) {
  if (!scope) return;

  scope.querySelectorAll('[data-sb-native-sale-badge]:not([data-sb-cart-sale-badge])').forEach((badge) => {
    if (hidden) {
      badge.setAttribute('hidden', '');
      badge.dataset.sbHiddenByMode = 'true';
    } else if (badge.dataset.sbHiddenByMode === 'true') {
      badge.removeAttribute('hidden');
      delete badge.dataset.sbHiddenByMode;
    }
  });

  scope.querySelectorAll('[data-sb-native-sticky-save]').forEach((badge) => {
    if (hidden) {
      badge.setAttribute('hidden', '');
      badge.dataset.sbHiddenByMode = 'true';
    } else if (badge.dataset.sbHiddenByMode === 'true') {
      badge.removeAttribute('hidden');
      delete badge.dataset.sbHiddenByMode;
    }
  });
}

function updateSbPriceElementContent(element, cents, options = {}) {
  if (!element) return;

  const srOnly = element.querySelector('.sr-only');
  const srOnlyClone = srOnly ? srOnly.cloneNode(true) : null;
  element.replaceChildren();

  if (srOnlyClone) {
    element.appendChild(srOnlyClone);
  } else if (options.srOnlyLabel) {
    const span = document.createElement('span');
    span.className = 'sr-only';
    span.textContent = options.srOnlyLabel;
    element.appendChild(span);
  }

  const money = formatSbMoney(cents);
  if (options.fromPrice) {
    element.insertAdjacentHTML('beforeend', formatSbFromPrice(money));
  } else {
    element.appendChild(document.createTextNode(money));
  }
}

function updateSbCampaignPriceElement(priceList, campaignPercentage) {
  const salePrice = parseInt(priceList.dataset.sbSalePrice, 10);
  const compareAtPrice = parseInt(priceList.dataset.sbCompareAtPrice, 10) || salePrice;
  if (!salePrice || !campaignPercentage) return;

  const pricing = getSbVariantPricing(salePrice, compareAtPrice, campaignPercentage);
  const salePriceEl = priceList.querySelector('[data-sb-sale-price-el]');
  const comparePriceEl = priceList.querySelector('[data-sb-compare-price-el]');
  const fromPrice = priceList.dataset.sbFromPrice === 'true';

  if (salePriceEl) {
    updateSbPriceElementContent(salePriceEl, pricing.discountedPrice, { fromPrice });
    applySbSalePriceClasses(salePriceEl, true);
  }

  if (comparePriceEl) {
    if (pricing.compareDisplay > pricing.discountedPrice) {
      updateSbPriceElementContent(comparePriceEl, pricing.compareDisplay);
      comparePriceEl.hidden = false;
      comparePriceEl.removeAttribute('hidden');
      comparePriceEl.classList.add('text-subdued', 'line-through');
      comparePriceEl.classList.remove('text-on-sale');
    } else {
      comparePriceEl.hidden = true;
      comparePriceEl.setAttribute('hidden', '');
    }
  }
}

function updateSbCampaignBadgeElement(badge, campaignPercentage) {
  const salePrice = parseInt(badge.dataset.sbVariantPrice, 10);
  const compareAtPrice = parseInt(badge.dataset.sbCompareAtPrice, 10) || salePrice;
  if (!salePrice || !campaignPercentage) return;

  const pricing = getSbVariantPricing(salePrice, compareAtPrice, campaignPercentage);
  badge.textContent = formatSbBadgeHtml(formatSbSavingsLabel(pricing));
}

function updateSbStickySave(scope, pricing) {
  scope?.querySelectorAll('[data-sb-sticky-save]').forEach((stickySave) => {
    stickySave.textContent = `-${formatSbSavingsLabel(pricing)}`;
    stickySave.removeAttribute('hidden');
  });
}

function getSbBannerPreviewState() {
  const campaigns = getSbCampaigns();
  const firstCampaign = campaigns[0];

  return {
    code: firstCampaign?.code || 'CODE',
    percentage: firstCampaign?.percentage || 20
  };
}

function isSbDesignMode() {
  return typeof Shopify !== 'undefined' && Shopify.designMode;
}

function updateSbCampaignBanners(state) {
  document.querySelectorAll('[data-sb-campaign-banner]').forEach((banner) => {
    const showBanner = state.active || isSbDesignMode();

    if (!showBanner) {
      banner.setAttribute('hidden', '');
      return;
    }

    banner.removeAttribute('hidden');

    const preview = isSbDesignMode() && !state.active ? getSbBannerPreviewState() : null;
    const code = preview ? preview.code : state.code;
    const percentage = preview ? preview.percentage : state.percentage;
    const codeEl = banner.querySelector('[data-sb-banner-code]');
    const headingEl = banner.querySelector('[data-sb-banner-heading]');

    if (codeEl) codeEl.textContent = code.toUpperCase();
    if (headingEl && percentage) {
      const template = headingEl.dataset.sbHeadingTemplate || '-{percentage}% AUF ALLES*';
      headingEl.textContent = template.replace('{percentage}', percentage);
    }
  });
}

function updateSbCampaignBadges(state) {
  document.querySelectorAll('[data-sb-campaign-badge]').forEach((badge) => {
    // Cart line items manage their own on-sale-badge / savings %.
    if (badge.closest('[data-sb-line-item]')) return;

    const scope = getSbBadgeScope(badge);
    const priceWrapper = badge.closest('[data-sb-price-wrapper]');
    const excluded = priceWrapper?.dataset.sbExcluded === 'true';
    const showBadge = state.active && state.percentage > 0 && !excluded;

    if (showBadge) {
      const salePrice = parseInt(badge.dataset.sbVariantPrice, 10);
      const compareAtPrice = parseInt(badge.dataset.sbCompareAtPrice, 10) || salePrice;
      const pricing = getSbVariantPricing(salePrice, compareAtPrice, state.percentage);

      updateSbCampaignBadgeElement(badge, state.percentage);
      badge.removeAttribute('hidden');

      const badgeList = badge.closest('badge-list');
      if (badgeList) badgeList.removeAttribute('hidden');

      setSbNativeSaleBadgesHidden(scope, true);
      updateSbStickySave(scope, pricing);
    } else {
      badge.setAttribute('hidden', '');
      setSbNativeSaleBadgesHidden(scope, false);

      scope?.querySelectorAll('[data-sb-sticky-save]').forEach((stickySave) => {
        stickySave.setAttribute('hidden', '');
      });
    }
  });
}

function getSbLineItemQuantity(lineItem) {
  const input = lineItem.querySelector('quantity-selector input, input[name="updates[]"]');
  const fromInput = parseInt(input?.value, 10);
  if (fromInput > 0) return fromInput;

  const fromData = parseInt(lineItem.dataset.sbQuantity, 10);
  return fromData > 0 ? fromData : 1;
}

function getSbLineItemPriceScale(lineItem) {
  if (lineItem.dataset.sbShowLineTotal !== 'true') return 1;
  return getSbLineItemQuantity(lineItem);
}

function updateSbLineItemPrices(state) {
  seedSbExclusionCacheFromDom();

  document.querySelectorAll('[data-sb-line-item]').forEach((lineItem) => {
    const excluded = lineItem.dataset.sbExcluded === 'true';
    const salePrice = parseInt(lineItem.dataset.sbSalePrice, 10);
    const compareAtPrice = parseInt(lineItem.dataset.sbCompareAtPrice, 10) || salePrice;
    const finalPrice = parseInt(lineItem.dataset.sbFinalPrice, 10);
    const originalPrice = parseInt(lineItem.dataset.sbOriginalPrice, 10);
    const priceScale = getSbLineItemPriceScale(lineItem);
    const saleEl = lineItem.querySelector('[data-sb-line-sale-el]');
    const compareEl = lineItem.querySelector('[data-sb-line-compare-el]');
    const priceList = saleEl?.closest('price-list') || lineItem;

    if (!saleEl || !salePrice) return;

    const restoreNativeLineItemPrice = () => {
      const restoredSaleUnit = excluded ? salePrice : (finalPrice || salePrice);
      const restoredCompareUnit = excluded ? compareAtPrice : (compareAtPrice > restoredSaleUnit ? compareAtPrice : restoredSaleUnit);
      const restoredSale = restoredSaleUnit * priceScale;
      const restoredCompare = restoredCompareUnit * priceScale;
      const restoredOnSale = restoredCompare > restoredSale;
      updateSbPriceElementContent(saleEl, restoredSale);
      applySbSalePriceClasses(saleEl, restoredOnSale);
      if (compareEl) {
        if (restoredCompare > restoredSale) {
          updateSbPriceElementContent(compareEl, restoredCompare);
          compareEl.hidden = false;
          compareEl.removeAttribute('hidden');
          compareEl.classList.add('text-subdued', 'line-through');
          compareEl.classList.remove('text-on-sale');
        } else {
          compareEl.hidden = true;
          compareEl.setAttribute('hidden', '');
        }
      }
      setSbNativeSaleBadgesHidden(priceList, false);
      lineItem.querySelectorAll('[data-sb-cart-sale-badge]').forEach((badge) => {
        if (!badge.textContent.trim()) {
          badge.hidden = true;
          badge.setAttribute('hidden', '');
        }
      });
      lineItem.querySelectorAll('[data-sb-campaign-badge], [data-sb-sticky-save]').forEach((el) => {
        el.setAttribute('hidden', '');
      });
      delete lineItem.dataset.sbPriceRewritten;
    };

    // Excluded products never get campaign pricing/badges, even if a discount was applied by mistake.
    if (excluded) {
      restoreNativeLineItemPrice();
      return;
    }

    // Shopify already applied a discount — mirror native compare-at + sale + badge styling.
    if (originalPrice > finalPrice) {
      const compareDisplay = Math.max(compareAtPrice, originalPrice) * priceScale;
      applySbLineItemSaleLook(lineItem, finalPrice * priceScale, compareDisplay, state);
      return;
    }

    if (!state.active || !state.percentage) {
      if (lineItem.dataset.sbPriceRewritten === 'true') {
        restoreNativeLineItemPrice();
      }
      return;
    }

    const pricing = getSbVariantPricing(salePrice, compareAtPrice, state.percentage);
    applySbLineItemSaleLook(
      lineItem,
      pricing.discountedPrice * priceScale,
      pricing.compareDisplay * priceScale,
      state
    );
    lineItem.dataset.sbPriceRewritten = 'true';
  });
}

function updateWishlistPrices(state) {
  const cards = document.querySelectorAll('wishlist-product-card');
  if (!cards.length) return;

  cards.forEach((card) => {
    attachWishlistCardListeners(card);
    updateSingleWishlistCard(card, state);
  });
}

function getWishlistCardRoot(card) {
  return card.shadowRoot || card;
}

function ensureWishlistShadowStyles(root) {
  if (!(root instanceof ShadowRoot)) return;
  if (root.querySelector('style[data-sb-wishlist-style]')) return;

  const style = document.createElement('style');
  style.setAttribute('data-sb-wishlist-style', '');
  style.textContent = `
    .wk-current-price.text-on-sale {
      color: rgb(var(--on-sale-text)) !important;
    }
    .wk-compare-price.text-subdued {
      color: rgb(var(--text-color) / .65);
    }
    .line-through {
      text-decoration: line-through;
    }
  `;
  root.appendChild(style);
}

function getWishlistSelectedVariant(card) {
  if (card.form?.selectedVariant) return card.form.selectedVariant;

  const item = card.wishlistItem || card.item || null;
  const product = item?.product || card.product || null;
  if (!product) return null;

  const root = getWishlistCardRoot(card);
  const idInput = root.querySelector?.('input[name="id"], select[name="id"]');
  const variantId = Number(
    card.form?.selectedVariantId
    || item?.variantId
    || item?.variant_id
    || idInput?.value
    || 0
  );

  if (variantId && Array.isArray(product.variants)) {
    const match = product.variants.find((variant) => Number(variant.id) === variantId);
    if (match) return match;
  }

  return item?.selectedVariant
    || item?.variant
    || product.selected_or_first_available_variant
    || product.variants?.[0]
    || null;
}

function getWishlistPricingFromElement(card) {
  const item = card.wishlistItem || card.item || card.productCard || null;
  const product = item?.product || card.product || null;
  if (!product) return null;

  const variant = getWishlistSelectedVariant(card);
  const salePrice = Number(variant?.price ?? product.price_min ?? product.price);
  if (!salePrice || Number.isNaN(salePrice)) return null;

  const rawCompare = Number(
    variant?.compare_at_price
    ?? product.compare_at_price_min
    ?? product.compare_at_price
    ?? salePrice
  );
  const compareAt = rawCompare > salePrice ? rawCompare : salePrice;

  return {
    salePrice,
    compareAt,
    variantId: variant?.id || null
  };
}

async function getWishlistPricingFromHandle(card) {
  const root = getWishlistCardRoot(card);
  const link = root.querySelector('a[href*="/products/"]')
    || card.querySelector?.('a[href*="/products/"]');
  if (!link) return null;

  const match = link.getAttribute('href')?.match(/\/products\/([^/?#]+)/);
  if (!match) return null;

  const handle = match[1];
  const idInput = root.querySelector?.('input[name="id"], select[name="id"]');
  const selectedVariantId = Number(idInput?.value || 0);
  const cacheKey = `sbWishlistPrice:${handle}:${selectedVariantId || 'default'}`;

  if (window.__sbWishlistPriceCache?.[cacheKey]) {
    return window.__sbWishlistPriceCache[cacheKey];
  }

  try {
    const product = await fetch(`${window.Shopify.routes.root}products/${handle}.js`).then((r) => r.json());
    const variant = (selectedVariantId && product.variants?.find((entry) => Number(entry.id) === selectedVariantId))
      || product.variants?.[0];
    const salePrice = Number(variant?.price ?? product.price);
    const rawCompare = Number(variant?.compare_at_price ?? product.compare_at_price ?? salePrice);
    const compareAt = rawCompare > salePrice ? rawCompare : salePrice;
    const pricing = { salePrice, compareAt, variantId: variant?.id || null };
    window.__sbWishlistPriceCache = window.__sbWishlistPriceCache || {};
    window.__sbWishlistPriceCache[cacheKey] = pricing;
    return pricing;
  } catch (error) {
    console.warn('SB Mode: wishlist product fetch failed', error);
    return null;
  }
}

function findWishlistPriceContainer(root) {
  return root.querySelector('.wk-price')
    || root.querySelector('[part="price"]')
    || root.querySelector('.wk-product-price')
    || root.querySelector('.wk-current-price')?.parentElement
    || root.querySelector('[class*="price"]');
}

function restoreWishlistPriceMarkup(container) {
  if (!container || container.dataset.sbWishlistRewritten !== 'true') return;
  if (container.dataset.sbWishlistOriginalHtml != null) {
    container.innerHTML = container.dataset.sbWishlistOriginalHtml;
  }
  delete container.dataset.sbWishlistRewritten;
  delete container.dataset.sbWishlistDiscounted;
  delete container.dataset.sbWishlistVariant;
  delete container.dataset.sbWishlistOriginalHtml;
}

function applyWishlistPriceMarkup(container, pricing) {
  if (!container || !pricing) return;

  if (container.dataset.sbWishlistRewritten !== 'true') {
    container.dataset.sbWishlistOriginalHtml = container.innerHTML;
  }

  // Preserve Wishlist King / existing typography classes; only apply sale/compare look.
  const existingSale = container.querySelector('.wk-current-price, [data-sb-wishlist-sale]');
  const existingCompare = container.querySelector('.wk-compare-price, [data-sb-wishlist-compare]');

  const saleClasses = new Set(
    (existingSale?.className || 'wk-current-price')
      .split(/\s+/)
      .filter(Boolean)
  );
  saleClasses.add('wk-current-price');
  saleClasses.add('text-on-sale');
  saleClasses.delete('text-subdued');
  saleClasses.delete('line-through');

  const compareClasses = new Set(
    (existingCompare?.className || existingSale?.className || 'wk-compare-price')
      .split(/\s+/)
      .filter(Boolean)
  );
  compareClasses.add('wk-compare-price');
  compareClasses.add('text-subdued');
  compareClasses.add('line-through');
  compareClasses.delete('text-on-sale');
  compareClasses.delete('wk-current-price');

  const compareHtml = pricing.compareDisplay > pricing.discountedPrice
    ? `<span class="${[...compareClasses].join(' ')}" data-sb-wishlist-compare>${formatSbMoney(pricing.compareDisplay)}</span>`
    : '';
  const saleHtml = `<span class="${[...saleClasses].join(' ')}" data-sb-wishlist-sale>${formatSbMoney(pricing.discountedPrice)}</span>`;

  container.innerHTML = `${compareHtml}${saleHtml}`;
  container.dataset.sbWishlistRewritten = 'true';
  container.dataset.sbWishlistDiscounted = String(pricing.discountedPrice);
  container.dataset.sbWishlistVariant = String(pricing.variantId || '');
}

async function updateSingleWishlistCard(card, state) {
  const root = getWishlistCardRoot(card);
  if (!root) return;

  ensureWishlistShadowStyles(root);

  // Theme wrappers inside wishlist (if any) are handled by updateSbPriceDisplays
  if (root.querySelector?.('[data-sb-price-wrapper]') || card.querySelector?.('[data-sb-price-wrapper]')) {
    return;
  }

  const container = findWishlistPriceContainer(root);
  const excluded = await ensureWishlistCardExclusion(card);

  // Still hydrating — leave native price and retry later.
  if (excluded === null) {
    restoreWishlistPriceMarkup(container);
    syncSbWishlistCardFormProperties(card, state, true);
    return;
  }

  // Keep campaign form properties in sync with exclusion state.
  syncSbWishlistCardFormProperties(card, state, excluded);

  if (!state.active || !state.percentage || excluded) {
    restoreWishlistPriceMarkup(container);
    card.dataset.sbWishlistHandled = 'true';
    return;
  }

  let rawPricing = getWishlistPricingFromElement(card);
  if (!rawPricing) {
    rawPricing = await getWishlistPricingFromHandle(card);
  }
  if (!rawPricing?.salePrice) return;

  const pricing = getSbVariantPricing(rawPricing.salePrice, rawPricing.compareAt, state.percentage);
  pricing.variantId = rawPricing.variantId;

  if (!container) return;

  // Skip no-op rewrites for the same variant/price to avoid fighting Lit renders.
  if (
    container.dataset.sbWishlistRewritten === 'true'
    && container.dataset.sbWishlistDiscounted === String(pricing.discountedPrice)
    && container.dataset.sbWishlistVariant === String(pricing.variantId || '')
    && container.querySelector('[data-sb-wishlist-sale]')
  ) {
    card.dataset.sbWishlistHandled = 'true';
    return;
  }

  applyWishlistPriceMarkup(container, pricing);
  card.dataset.sbWishlistHandled = 'true';
}

function attachWishlistCardListeners(card) {
  if (!(card instanceof Element) || card.dataset.sbWishlistListener === 'true') return;
  card.dataset.sbWishlistListener = 'true';

  const refresh = () => {
    delete card.dataset.sbWishlistHandled;
    const root = getWishlistCardRoot(card);
    const container = root ? findWishlistPriceContainer(root) : null;
    if (container) {
      delete container.dataset.sbWishlistRewritten;
      delete container.dataset.sbWishlistDiscounted;
      delete container.dataset.sbWishlistVariant;
    }

    // Wishlist King re-renders via Lit after option changes — wait for that, then re-apply.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateSingleWishlistCard(card, getSbState());
      });
    });
  };

  card.addEventListener('change', (event) => {
    if (event.target?.closest?.('.wk-form') || event.target?.name?.startsWith?.('option') || event.target?.name === 'id') {
      refresh();
      // Wishlist King also persists the variant async — re-apply after that settles.
      setTimeout(refresh, 350);
    }
  });

  card.addEventListener('variant:change', refresh);
}

function scheduleWishlistPriceRetries() {
  const state = getSbState();
  if (!state.active) return;
  if (document.documentElement.dataset.sbWishlistRetry === 'true') return;
  document.documentElement.dataset.sbWishlistRetry = 'true';

  let attempts = 0;
  const timer = setInterval(() => {
    attempts += 1;
    updateWishlistPrices(getSbState());
    const pending = [...document.querySelectorAll('wishlist-product-card')]
      .some((card) => {
        if (card.dataset.sbExcluded === 'true') return false;
        if (!card.hasAttribute('data-sb-excluded')) return true;
        const root = getWishlistCardRoot(card);
        const container = root ? findWishlistPriceContainer(root) : null;
        return !container?.querySelector('[data-sb-wishlist-sale]');
      });
    if (!pending || attempts >= 40) {
      clearInterval(timer);
      delete document.documentElement.dataset.sbWishlistRetry;
    }
  }, 300);
}

let sbCartDiscountSyncing = false;
let sbCartDiscountLastCode = '';
let sbCartDiscountPromise = null;

function cartHasSbDiscount(cart, code) {
  if (!cart || !code) return false;
  const normalized = code.toLowerCase();

  const codes = cart.discount_codes || [];
  if (codes.some((entry) => entry.code?.toLowerCase() === normalized)) return true;

  const cartLevel = cart.cart_level_discount_applications || [];
  if (cartLevel.some((entry) => entry.title?.toLowerCase() === normalized)) return true;

  return (cart.items || []).some((item) =>
    (item.discounts || []).some((discount) => discount.title?.toLowerCase() === normalized)
  );
}

function cartItemHasSbProperty(item, code) {
  if (!item?.properties || !code) return false;
  const key = `_${code}`;
  const value = item.properties[key];
  return value !== undefined && value !== null && value !== '';
}

async function fetchSbCart() {
  const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
    headers: { Accept: 'application/json' }
  });
  if (!response.ok) throw new Error(`SB Mode cart fetch failed: ${response.status}`);
  return response.json();
}

async function applySbDiscountCodeToCart(code) {
  const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({ discount: code })
  });
  if (!response.ok) throw new Error(`SB Mode discount apply failed: ${response.status}`);
  return response.json();
}

async function ensureSbLineItemProperties(cart, code) {
  const propertyKey = `_${code}`;
  const items = cart.items || [];
  const itemsToUpdate = [];

  for (const item of items) {
    if (item.final_price === 0) continue;
    if (isBundleCartItem(item)) continue;

    const excluded = await resolveSbExclusion({
      handle: item.handle,
      productId: item.product_id,
      variantId: item.variant_id
    });
    const hasProp = cartItemHasSbProperty(item, code);
    const properties = { ...(item.properties || {}) };

    // Unknown exclusion — never add the campaign property.
    if (excluded === null) {
      if (!hasProp) continue;
      // Prefer removing an uncertain property over accidentally discounting excluded products.
      properties[propertyKey] = '';
      itemsToUpdate.push({ item, properties });
      continue;
    }

    if (excluded === true) {
      if (!hasProp) continue;
      // Clear the campaign property so automatic discounts stop applying.
      properties[propertyKey] = '';
      itemsToUpdate.push({ item, properties });
      continue;
    }

    if (hasProp) continue;
    properties[propertyKey] = 'true';
    itemsToUpdate.push({ item, properties });
  }

  if (!itemsToUpdate.length) return { changed: false, cart };

  // Update missing/cleared properties in parallel for speed
  await Promise.all(itemsToUpdate.map(({ item, properties }) => (
    fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        properties
      })
    })
  )));

  const updatedCart = await fetchSbCart();
  return { changed: true, cart: updatedCart };
}

/**
 * Safeguard: when SB Mode is active and the cart has items, ensure the campaign
 * discount code is applied and line items carry the campaign property.
 * @param {{ force?: boolean, cart?: object, silent?: boolean }} [options]
 */
async function ensureSbCartDiscount(options = {}) {
  const state = getSbState();
  if (!state.active || !state.code) return null;

  // Await any in-flight sync first (checkout / ATC share the same promise)
  if (sbCartDiscountPromise) {
    const inFlightCart = await sbCartDiscountPromise;
    if (!options.force) return inFlightCart;

    const latest = inFlightCart || await fetchSbCart().catch(() => null);
    if (latest && cartHasSbDiscount(latest, state.code)) {
      // Still backfill missing properties if needed
      if (latest.item_count) {
        const propsResult = await ensureSbLineItemProperties(latest, state.code);
        if (propsResult.changed && !options.silent) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          updateSbLineItemPrices(state);
        }
        return propsResult.cart || latest;
      }
      return latest;
    }
  }

  if (sbCartDiscountSyncing && !options.force) return null;

  sbCartDiscountSyncing = true;
  sbCartDiscountPromise = (async () => {
    try {
      let cart = options.cart || await fetchSbCart();
      if (!cart.item_count) {
        sbCartDiscountLastCode = '';
        return cart;
      }

      let changed = false;

      // Apply discount code first — this is what checkout needs
      if (!cartHasSbDiscount(cart, state.code)) {
        cart = await applySbDiscountCodeToCart(state.code);
        sbCartDiscountLastCode = state.code;
        changed = true;
      } else {
        sbCartDiscountLastCode = state.code;
      }

      const propsResult = await ensureSbLineItemProperties(cart, state.code);
      if (propsResult.changed) {
        cart = propsResult.cart;
        changed = true;
      }

      if (changed && !options.silent) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        // Update visible prices immediately from the cart we already have
        updateSbLineItemPrices(state);
      }

      return cart;
    } catch (error) {
      console.warn('SB Mode: cart discount safeguard failed', error);
      return null;
    } finally {
      sbCartDiscountSyncing = false;
      sbCartDiscountPromise = null;
    }
  })();

  return sbCartDiscountPromise;
}

function extractSbCartAddVariantIds(body) {
  const ids = [];

  const pushId = (value) => {
    const id = Number(value);
    if (id && !ids.includes(id)) ids.push(id);
  };

  if (typeof body === 'string') {
    try {
      return extractSbCartAddVariantIds(JSON.parse(body));
    } catch {
      const params = new URLSearchParams(body);
      if (params.has('id')) pushId(params.get('id'));
      for (const [key, value] of params.entries()) {
        if (/^items\[\d+]\[id]$/.test(key) || key === 'items[][id]') pushId(value);
      }
      return ids;
    }
  }

  if (body instanceof FormData) {
    if (body.has('id')) pushId(body.get('id'));
    for (const [key, value] of body.entries()) {
      if (/^items\[\d+]\[id]$/.test(key) || key === 'items[][id]') pushId(value);
    }
    return ids;
  }

  if (body && typeof body === 'object') {
    if (Array.isArray(body.items)) {
      body.items.forEach((item) => pushId(item?.id));
      return ids;
    }
    pushId(body.id);
  }

  return ids;
}

function getSbExclusionSync({ handle = null, productId = null, variantId = null } = {}) {
  seedSbExclusionCacheFromDom();
  const cached = getSbExclusionFromCache({ handle, productId, variantId });
  if (cached !== null) return cached;

  if (variantId != null && variantId !== '') {
    const wishlistCards = document.querySelectorAll('wishlist-product-card');
    for (const card of wishlistCards) {
      const root = getWishlistCardRoot(card);
      const idInput = root.querySelector?.('input[name="id"], select[name="id"]');
      const selected = getWishlistSelectedVariant(card);
      const matchesVariant = (selected && String(selected.id) === String(variantId))
        || (idInput && String(idInput.value) === String(variantId));

      if (!matchesVariant) continue;

      if (card.hasAttribute('data-sb-excluded')) {
        const excluded = card.dataset.sbExcluded === 'true';
        setSbExclusionCache({
          variantId,
          handle: card.dataset.sbHandle || getWishlistCardHandle(card) || handle,
          excluded
        });
        return excluded;
      }
    }
  }

  return getSbExclusionFromDom({ handle, productId, variantId });
}

function shouldInjectSbPropertyForVariantSync(variantId) {
  if (!variantId) return true;

  // If this variant belongs to a wishlist card that is not resolved yet, do not inject.
  const wishlistCards = document.querySelectorAll('wishlist-product-card');
  for (const card of wishlistCards) {
    const root = getWishlistCardRoot(card);
    const idInput = root.querySelector?.('input[name="id"], select[name="id"]');
    const selected = getWishlistSelectedVariant(card);
    const matchesVariant = (selected && String(selected.id) === String(variantId))
      || (idInput && String(idInput.value) === String(variantId));
    if (!matchesVariant) continue;

    if (!card.hasAttribute('data-sb-excluded')) return false;
    return card.dataset.sbExcluded !== 'true';
  }

  const excluded = getSbExclusionSync({ variantId });
  // null = unknown — keep legacy inject behavior; ensureSbLineItemProperties will clean up.
  if (excluded === null) return true;
  return !excluded;
}

async function shouldInjectSbPropertyForVariant(variantId) {
  if (!variantId) return true;

  // Prefer wishlist card state when present (includes shadow DOM forms).
  const wishlistCards = document.querySelectorAll('wishlist-product-card');
  for (const card of wishlistCards) {
    const root = getWishlistCardRoot(card);
    const idInput = root.querySelector?.('input[name="id"], select[name="id"]');
    const selected = getWishlistSelectedVariant(card);
    const matchesVariant = (selected && String(selected.id) === String(variantId))
      || (idInput && String(idInput.value) === String(variantId));
    if (!matchesVariant) continue;

    const excluded = await ensureWishlistCardExclusion(card);
    if (excluded === null) return false; // safer: don't discount until known
    return !excluded;
  }

  const excluded = await resolveSbExclusion({ variantId });
  if (excluded === null) return false;
  return !excluded;
}

function isBundleCartItem(item) {
  if (!item?.properties || typeof item.properties !== 'object') return false;
  const mainLabel = window.themeVariables?.settings?.mainProductPropertyLabel || '_Main';
  const addonsLabel = window.themeVariables?.settings?.addonsPropertyLabel || '_Addon';
  return Boolean(item.properties[mainLabel] || item.properties[addonsLabel]);
}

function applySbPropertyInjection(body, code, shouldInjectFn) {
  const propertyKey = `_${code}`;
  const variantIds = extractSbCartAddVariantIds(body);
  const exclusionByVariant = {};

  variantIds.forEach((variantId) => {
    exclusionByVariant[String(variantId)] = !shouldInjectFn(variantId);
  });

  const isExcludedVariant = (variantId) => exclusionByVariant[String(variantId)] === true;

  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      const next = applySbPropertyInjection(parsed, code, shouldInjectFn);
      return JSON.stringify(next);
    } catch {
      const params = new URLSearchParams(body);
      const variantId = Number(params.get('id') || 0);
      if (variantId && isExcludedVariant(variantId)) {
        params.delete(`properties[${propertyKey}]`);
        return params.toString();
      }

      if (body.includes(`properties[${propertyKey}]`) || body.includes(`properties%5B${propertyKey}%5D`)) {
        return body;
      }

      let mutated = false;
      for (const [key, value] of [...params.entries()]) {
        const match = key.match(/^items\[(\d+)]\[id]$/);
        if (!match) continue;
        const index = match[1];
        const itemVariantId = Number(value);
        if (!itemVariantId || isExcludedVariant(itemVariantId)) continue;
        const propKey = `items[${index}][properties][${propertyKey}]`;
        if (!params.has(propKey)) {
          params.set(propKey, 'true');
          mutated = true;
        }
      }

      if (mutated) return params.toString();
      if (variantId && !isExcludedVariant(variantId)) {
        return `${body}&${encodeURIComponent(`properties[${propertyKey}]`)}=${encodeURIComponent('true')}`;
      }
      return body;
    }
  }

  if (body instanceof FormData) {
    const variantId = Number(body.get('id') || 0);
    if (variantId && !isExcludedVariant(variantId)) {
      if (![...body.keys()].some((key) => key === `properties[${propertyKey}]`)) {
        body.set(`properties[${propertyKey}]`, 'true');
      }
    } else if (variantId && isExcludedVariant(variantId)) {
      body.delete(`properties[${propertyKey}]`);
    }

    for (const [key, value] of [...body.entries()]) {
      const match = key.match(/^items\[(\d+)]\[id]$/);
      if (!match) continue;
      const index = match[1];
      const itemVariantId = Number(value);
      const propKey = `items[${index}][properties][${propertyKey}]`;
      if (!itemVariantId || isExcludedVariant(itemVariantId)) {
        body.delete(propKey);
        continue;
      }
      if (!body.has(propKey)) body.set(propKey, 'true');
    }
    return body;
  }

  if (body && typeof body === 'object') {
    if (Array.isArray(body.items)) {
      body.items = body.items.map((item) => {
        if (isBundleCartItem(item) || isExcludedVariant(item?.id)) {
          if (!item?.properties) return item;
          const properties = { ...(item.properties || {}) };
          delete properties[propertyKey];
          return { ...item, properties };
        }
        return {
          ...item,
          properties: {
            ...(item.properties || {}),
            [propertyKey]: 'true'
          }
        };
      });
      return body;
    }

    if (isBundleCartItem(body) || isExcludedVariant(body.id)) {
      if (body.properties) {
        const properties = { ...body.properties };
        delete properties[propertyKey];
        body.properties = properties;
      }
      return body;
    }

    body.properties = {
      ...(body.properties || {}),
      [propertyKey]: 'true'
    };
    return body;
  }

  return body;
}

async function injectSbPropertiesIntoCartAddBody(body, code) {
  const variantIds = extractSbCartAddVariantIds(body);
  const injectByVariant = {};

  await Promise.all(variantIds.map(async (variantId) => {
    injectByVariant[String(variantId)] = await shouldInjectSbPropertyForVariant(variantId);
  }));

  return applySbPropertyInjection(body, code, (variantId) => {
    if (!variantId) return true;
    if (Object.prototype.hasOwnProperty.call(injectByVariant, String(variantId))) {
      return injectByVariant[String(variantId)];
    }
    return shouldInjectSbPropertyForVariantSync(variantId);
  });
}

function injectSbPropertiesIntoCartAddBodySync(body, code) {
  return applySbPropertyInjection(body, code, shouldInjectSbPropertyForVariantSync);
}

function isCartAddRequest(input, init = {}) {
  const url = typeof input === 'string' ? input : input?.url || '';
  if (!/\/cart\/add(?:\.js)?(?:\?|$)/.test(url)) return false;
  const method = (init.method || input?.method || 'GET').toUpperCase();
  return method === 'POST';
}

function installSbCartAddInterceptor() {
  if (window.__sbFetchPatched) return;
  window.__sbFetchPatched = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async function sbFetch(input, init = {}) {
    const state = getSbState();
    const isCartAdd = isCartAddRequest(input, init);

    if (state.active && state.code && isCartAdd) {
      const nextInit = { ...init };
      if (nextInit.body != null) {
        nextInit.body = await injectSbPropertiesIntoCartAddBody(nextInit.body, state.code);
        // If we mutated a plain object into needing JSON, ensure header
        if (typeof nextInit.body === 'object' && !(nextInit.body instanceof FormData) && typeof nextInit.body !== 'string') {
          nextInit.body = JSON.stringify(nextInit.body);
          nextInit.headers = new Headers(nextInit.headers || {});
          if (!nextInit.headers.has('Content-Type')) {
            nextInit.headers.set('Content-Type', 'application/json');
          }
        }
      }

      const response = await nativeFetch(input, nextInit);

      // Wait for discount code so checkout cannot race ahead of an empty cart discount
      try {
        await ensureSbCartDiscount({ force: true });
      } catch (error) {
        console.warn('SB Mode: post-add discount sync failed', error);
      }

      return response;
    }

    return nativeFetch(input, init);
  };

  // Wishlist King / older scripts may use XHR for /cart/add.js
  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function sbXhrOpen(method, url, ...rest) {
    this.__sbUrl = typeof url === 'string' ? url : String(url);
    this.__sbMethod = method;
    return nativeOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function sbXhrSend(body) {
    const state = getSbState();
    const isCartAdd = state.active
      && state.code
      && /\/cart\/add(?:\.js)?(?:\?|$)/.test(this.__sbUrl || '')
      && String(this.__sbMethod || 'GET').toUpperCase() === 'POST';

    let payload = body;
    if (isCartAdd && payload != null) {
      // Keep XHR send synchronous; async exclusion is handled by fetch + cart property sync.
      payload = injectSbPropertiesIntoCartAddBodySync(payload, state.code);
      if (typeof payload === 'object' && !(payload instanceof FormData) && typeof payload !== 'string') {
        payload = JSON.stringify(payload);
        try {
          this.setRequestHeader('Content-Type', 'application/json');
        } catch {
          // Header may already be set.
        }
      }
    }

    if (isCartAdd) {
      this.addEventListener('loadend', () => {
        ensureSbCartDiscount({ force: true }).catch(() => {});
      }, { once: true });
    }

    return nativeSend.call(this, payload);
  };
}

function syncSbWishlistCardFormProperties(card, state, excluded) {
  const root = card.shadowRoot || card;
  const forms = root.querySelectorAll('form.wk-form, form[action*="/cart/add"]');

  forms.forEach((form) => {
    form.querySelectorAll('[data-sb-campaign-property]').forEach((input) => input.remove());
    // Only attach campaign property when product is definitively NOT excluded.
    if (!state.active || !state.code || excluded !== false) return;

    const propertyName = `_${state.code}`;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${propertyName}]`;
    input.value = 'true';
    input.setAttribute('data-sb-campaign-property', '');
    form.appendChild(input);
  });
}

function syncSbWishlistFormProperties() {
  const state = getSbState();
  const cards = document.querySelectorAll('wishlist-product-card');

  cards.forEach(async (card) => {
    const excluded = state.active ? await ensureWishlistCardExclusion(card) : false;
    syncSbWishlistCardFormProperties(card, state, excluded);
  });
}

async function guardSbCheckout(event) {
  const state = getSbState();
  if (!state.active || !state.code) return;

  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const checkoutEl = target.closest(
    'button[name="checkout"], input[name="checkout"], [href="/checkout"], a[href*="/checkout"], button[href*="/checkout"]'
  );
  if (!checkoutEl) return;

  // Already handled this click chain
  if (checkoutEl.dataset.sbCheckoutReady === 'true') {
    delete checkoutEl.dataset.sbCheckoutReady;
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();

  checkoutEl.setAttribute('aria-busy', 'true');
  try {
    await ensureSbCartDiscount({ force: true, silent: true });
    if (typeof window.__preorderPrepareCheckout === 'function') {
      await window.__preorderPrepareCheckout();
    }
  } finally {
    checkoutEl.removeAttribute('aria-busy');
  }

  checkoutEl.dataset.sbCheckoutReady = 'true';

  if (checkoutEl.matches('a[href]')) {
    window.location.href = checkoutEl.href;
    return;
  }

  if (checkoutEl.form) {
    checkoutEl.form.requestSubmit(checkoutEl);
    return;
  }

  checkoutEl.click();
}

function updateSbPriceDisplays() {
  const state = getSbState();

  document.documentElement.classList.toggle('sb-mode-active', state.active);
  seedSbExclusionCacheFromDom();

  document.querySelectorAll('[data-sb-price-wrapper]').forEach((wrapper) => {
    const excluded = wrapper.dataset.sbExcluded === 'true';
    const normalEl = wrapper.querySelector('.sb-price-normal');
    const campaignEl = wrapper.querySelector('.sb-price-campaign');
    const showCampaign = state.active && !excluded;

    if (normalEl) {
      if (showCampaign) normalEl.setAttribute('hidden', '');
      else normalEl.removeAttribute('hidden');
    }

    if (campaignEl) {
      if (showCampaign) {
        campaignEl.removeAttribute('hidden');
        campaignEl.querySelectorAll('sb-campaign-price-list').forEach((priceList) => {
          updateSbCampaignPriceElement(priceList, state.percentage);
        });
      } else {
        campaignEl.setAttribute('hidden', '');
      }
    }
  });

  updateSbCampaignBadges(state);
  updateSbCampaignBanners(state);
  updateSbLineItemPrices(state);
  updateWishlistPrices(state);
  scheduleWishlistPriceRetries();
  syncSbCartProperties();
  syncSbWishlistFormProperties();
}

function syncSbCartProperties() {
  const state = getSbState();

  document.querySelectorAll('form[is="product-form"]').forEach((form) => {
    form.querySelectorAll('[data-sb-campaign-property]').forEach((input) => input.remove());

    if (!state.active) return;

    if (form.querySelector('[data-sb-campaign-disabled]')) return;

    const productId = form.dataset.productId;
    const wrapper = productId
      ? document.querySelector(`[data-sb-price-wrapper][data-product-id="${productId}"]`)
      : form.closest('[data-sb-price-wrapper]');

    const excluded = wrapper?.dataset.sbExcluded === 'true';
    if (excluded) return;

    const propertyName = `_${state.code}`;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${propertyName}]`;
    input.value = 'true';
    input.setAttribute('data-sb-campaign-property', '');
    form.appendChild(input);
  });
}

/**
 * Activate SB Mode via ?sb_activate=CODE URL parameter.
 * Example: /collections/2026?sb_activate=sb2026
 */
function syncSbModeFromUrl() {
  const params = new URLSearchParams(window.location.search);

  if (!params.has('sb_activate')) return false;

  const code = params.get('sb_activate');
  if (!activateSbMode(code)) return false;

  params.delete('sb_activate');
  const query = params.toString();
  const newUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
  window.history.replaceState({}, '', newUrl);
  return true;
}

function applySbVariantPricing(formId, variant, productId) {
  const state = getSbState();
  if (!state.active || !variant) return;

  const compareAtPrice = variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
  const pricing = getSbVariantPricingFromVariant(variant, state.percentage);
  const priceLists = new Set();

  if (formId) {
    document.querySelectorAll(`sb-campaign-price-list[form="${formId}"]`).forEach((el) => priceLists.add(el));
  }

  if (productId) {
    document.querySelectorAll(`[data-sb-price-wrapper][data-product-id="${productId}"] sb-campaign-price-list`).forEach((el) => {
      priceLists.add(el);
    });
  }

  priceLists.forEach((priceList) => {
    priceList.dataset.sbSalePrice = String(variant.price);
    priceList.dataset.sbCompareAtPrice = String(compareAtPrice);
    updateSbCampaignPriceElement(priceList, state.percentage);
  });

  const badges = new Set();
  if (formId) {
    document.querySelectorAll(`[data-sb-campaign-badge][data-sb-form="${formId}"]`).forEach((el) => badges.add(el));
  }
  if (productId) {
    document.querySelectorAll(`[data-sb-price-wrapper][data-product-id="${productId}"] [data-sb-campaign-badge]`).forEach((el) => {
      badges.add(el);
    });
    document.querySelectorAll(`[data-sb-campaign-badge][data-sb-form]`).forEach((badge) => {
      const scope = getSbBadgeScope(badge);
      const wrapper = scope?.closest?.('[data-sb-price-wrapper]') || badge.closest('[data-sb-price-wrapper]');
      if (wrapper?.dataset.productId === String(productId)) badges.add(badge);
    });
  }

  badges.forEach((badge) => {
    badge.dataset.sbVariantPrice = String(variant.price);
    badge.dataset.sbCompareAtPrice = String(compareAtPrice);
    updateSbCampaignBadgeElement(badge, state.percentage);
    updateSbStickySave(getSbBadgeScope(badge), pricing);
  });
}

function onSbVariantChange(event) {
  const state = getSbState();
  if (!state.active) return;

  const variant = event.detail?.variant;
  if (!variant) return;

  const form = event.target?.closest?.('form') || (event.target?.tagName === 'FORM' ? event.target : null);
  const formId = form?.id || event.currentTarget?.id;
  const productId = form?.dataset?.productId || event.detail?.product?.id;

  // Run after theme SalePrice/CompareAtPrice listeners so campaign prices win.
  // A second pass covers contexts that repaint one frame later.
  requestAnimationFrame(() => {
    applySbVariantPricing(formId, variant, productId);
    requestAnimationFrame(() => {
      applySbVariantPricing(formId, variant, productId);
    });
  });
}

function attachSbVariantListeners() {
  if (document.documentElement.dataset.sbVariantListener === 'true') return;
  document.documentElement.dataset.sbVariantListener = 'true';
  document.addEventListener('variant:change', onSbVariantChange);
}

function observeSbDynamicContent() {
  if (document.documentElement.dataset.sbObserver === 'true') return;
  document.documentElement.dataset.sbObserver = 'true';

  let scheduled = false;
  const scheduleRefresh = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      attachSbVariantListeners();
      updateSbPriceDisplays();
    });
  };

  const observeWishlistCard = (card) => {
    if (!(card instanceof Element) || card.tagName !== 'WISHLIST-PRODUCT-CARD') return;
    attachWishlistCardListeners(card);
    if (card.dataset.sbShadowObserved === 'true') return;
    card.dataset.sbShadowObserved = 'true';

    const tryObserveShadow = () => {
      if (card.shadowRoot) {
        ensureWishlistShadowStyles(card.shadowRoot);
        observer.observe(card.shadowRoot, { childList: true, subtree: true, characterData: true });
        scheduleRefresh();
        return true;
      }
      return false;
    };

    if (!tryObserveShadow()) {
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        if (tryObserveShadow() || attempts >= 20) clearInterval(timer);
      }, 250);
    }
  };

  const observer = new MutationObserver((mutations) => {
    let shouldRefresh = false;

    for (const mutation of mutations) {
      // Wishlist King Lit re-render overwrote our discounted price — re-apply.
      if (mutation.target instanceof Element || mutation.target?.parentElement) {
        const priceHost = (mutation.target instanceof Element ? mutation.target : mutation.target.parentElement)
          ?.closest?.('.wk-price, wishlist-product-card');
        if (priceHost) {
          const card = priceHost.closest?.('wishlist-product-card')
            || (priceHost.tagName === 'WISHLIST-PRODUCT-CARD' ? priceHost : null)
            || mutation.target?.getRootNode?.()?.host;
          if (card?.tagName === 'WISHLIST-PRODUCT-CARD' && card.dataset.sbExcluded !== 'true') {
            const root = getWishlistCardRoot(card);
            const container = findWishlistPriceContainer(root);
            if (container && !container.querySelector('[data-sb-wishlist-sale]')) {
              delete card.dataset.sbWishlistHandled;
              delete container.dataset.sbWishlistRewritten;
              shouldRefresh = true;
            }
          }
        }
      }

      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.tagName === 'WISHLIST-PRODUCT-CARD') observeWishlistCard(node);
        node.querySelectorAll?.('wishlist-product-card').forEach(observeWishlistCard);

        if (
          node.matches?.('[data-sb-price-wrapper], [data-sb-line-item], [data-sb-campaign-badge], sb-campaign-price-list, wishlist-product-card, quick-buy-modal, product-recommendations, predictive-search, .predictive-search__content, .predictive-search__products')
          || node.querySelector?.('[data-sb-price-wrapper], [data-sb-line-item], [data-sb-campaign-badge], sb-campaign-price-list, wishlist-product-card, .predictive-search__products')
        ) {
          shouldRefresh = true;
        }
      }
    }

    if (shouldRefresh) scheduleRefresh();
  });

  observer.observe(document.body, { childList: true, subtree: true });
  document.querySelectorAll('wishlist-product-card').forEach(observeWishlistCard);
  document.querySelectorAll('predictive-search').forEach((el) => {
    el.addEventListener('predictive-search:updated', scheduleRefresh);
  });
}

function initSbMode() {
  installSbCartAddInterceptor();
  attachSbVariantListeners();
  syncSbModeFromUrl();
  updateSbPriceDisplays();
  observeSbDynamicContent();
  ensureSbCartDiscount({ force: true });

  document.documentElement.addEventListener('sb-mode:refresh', () => {
    attachSbVariantListeners();
    updateSbPriceDisplays();
    ensureSbCartDiscount({ force: true });
  });

  document.addEventListener('cart:change', (event) => {
    const cartFromEvent = event.detail?.cart;
    // Apply discount immediately — no throttle, no waiting for section refresh
    ensureSbCartDiscount({ force: true, cart: cartFromEvent }).then(() => {
      updateSbPriceDisplays();
    });
  });

  document.addEventListener('cart:refresh', () => {
    requestAnimationFrame(() => updateSbPriceDisplays());
  });

  document.documentElement.addEventListener('theme:loading:end', () => {
    requestAnimationFrame(() => updateSbPriceDisplays());
  });

  document.addEventListener('variant:add', (event) => {
    const cartFromEvent = event.detail?.cart;
    ensureSbCartDiscount({ force: true, cart: cartFromEvent });
  });

  // Block checkout until discount code is confirmed on the cart
  document.addEventListener('click', guardSbCheckout, true);

  if (isSbDesignMode()) {
    document.addEventListener('shopify:section:load', () => {
      attachSbVariantListeners();
      updateSbPriceDisplays();
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSbMode);
} else {
  initSbMode();
}

// Patch fetch as early as possible so wishlist ATC cannot race ahead of SB Mode
installSbCartAddInterceptor();

window.SbMode = {
  getState: getSbState,
  activate: activateSbMode,
  deactivate: deactivateSbMode,
  refresh: updateSbPriceDisplays,
  ensureCartDiscount: ensureSbCartDiscount
};
